import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, ChevronDown, Calendar, MessageSquare } from "lucide-react";
import { designs, categories } from "../data/designs";
import DesignCard from "../components/DesignCard";
import Seo from "../components/Seo";
import { businessLd, breadcrumbLd } from "../components/StructuredData";

const SORT_OPTIONS = [
  { label: "Featured", value: "featured" },
  { label: "Name: A–Z", value: "name-asc" },
];

export default function PortfolioPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sort, setSort] = useState("featured");
  const [filterOpen, setFilterOpen] = useState(false);
  const activeCategory = searchParams.get("category") || "all";

  const setCategory = (slug) => {
    if (slug === "all") searchParams.delete("category");
    else searchParams.set("category", slug);
    setSearchParams(searchParams);
  };

  const filtered = designs
    .filter((p) => activeCategory === "all" || p.category === activeCategory)
    .sort((a, b) => {
      if (sort === "name-asc") return a.name.localeCompare(b.name);
      // featured first
      return Number(!!b.featured) - Number(!!a.featured);
    });

  const activeCategoryName =
    activeCategory === "all"
      ? "All Designs"
      : categories.find((c) => c.slug === activeCategory)?.name || activeCategory;

  const seoTitle =
    activeCategory === "all"
      ? "Portfolio — Bespoke Furniture & Interior Designs"
      : `${activeCategoryName} — Bespoke Designs by Ananda Paudel`;
  const seoDesc =
    activeCategory === "all"
      ? "Reference designs from past projects across Nepal — sofas, wardrobes, beds, dressing tables, study tables, and kids' furniture. Each design is built to your specs."
      : `Reference ${activeCategoryName.toLowerCase()} designs by Ananda Paudel — bespoke, factory-direct, built to your dimensions and finishes.`;

  return (
    <>
      <Seo
        title={seoTitle}
        description={seoDesc}
        path={`/portfolio${activeCategory === "all" ? "" : `?category=${activeCategory}`}`}
        jsonLd={[
          businessLd(),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Portfolio", path: "/portfolio" },
          ]),
        ]}
      />
      <div className="min-h-screen bg-cream-50 pt-20 sm:pt-24">
        <div className="bg-cream-100 border-b border-cream-200 py-10 sm:py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <p className="font-sans text-xs tracking-widest text-walnut-400 uppercase mb-2">
              Portfolio · Reference Designs
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl text-matte-900 font-light">
              {activeCategoryName}
            </h1>
            <p className="font-sans text-sm sm:text-base text-matte-500 mt-3 max-w-2xl leading-relaxed">
              These are reference designs — past pieces and starting points
              for new ones. Save the ones you love to your inspiration list,
              then bring them into your free consultation. Every piece is
              built to your space, dimensions, and finishes.
            </p>
            <div className="flex items-center gap-2 mt-4">
              <div className="w-2 h-2 rounded-full bg-walnut-400" />
              <span className="font-sans text-xs text-matte-400">
                {filtered.length} reference design{filtered.length === 1 ? "" : "s"} · All bespoke · Save to inspiration list
              </span>
            </div>
          </div>
        </div>

        <div className="bg-matte-900 text-cream-300">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-8">
            <span className="font-sans text-xs tracking-widest text-walnut-400 uppercase flex-shrink-0">
              How it works
            </span>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-8 font-sans text-xs text-matte-400">
              <span>
                <span className="text-walnut-300 font-medium">01 · Browse</span> — save designs you like
              </span>
              <span>
                <span className="text-walnut-300 font-medium">02 · Consult</span> — free 30-min call with Ananda
              </span>
              <span>
                <span className="text-walnut-300 font-medium">03 · Design</span> — tailored to your space &amp; budget
              </span>
              <span>
                <span className="text-walnut-300 font-medium">04 · Build</span> — factory-direct, delivered home
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-56 flex-shrink-0">
              <button
                className="lg:hidden flex items-center gap-2 border border-cream-300 px-4 py-2.5 text-sm font-sans text-matte-700 mb-5 rounded-sm"
                onClick={() => setFilterOpen(!filterOpen)}
              >
                <SlidersHorizontal size={15} />
                Filter by Category
                <ChevronDown
                  size={14}
                  className={`ml-auto transition-transform ${filterOpen ? "rotate-180" : ""}`}
                />
              </button>

              <div className={`${filterOpen ? "block" : "hidden"} lg:block`}>
                <div className="mb-8">
                  <h3 className="font-sans text-xs tracking-widest text-matte-400 uppercase mb-4">
                    Category
                  </h3>
                  <div className="space-y-1">
                    <button
                      onClick={() => setCategory("all")}
                      className={`w-full text-left font-sans text-sm px-3 py-2.5 rounded-sm transition-colors ${
                        activeCategory === "all"
                          ? "bg-matte-900 text-cream-100"
                          : "text-matte-600 hover:bg-cream-200"
                      }`}
                    >
                      All Designs
                    </button>
                    {categories.map((c) => (
                      <button
                        key={c.slug}
                        onClick={() => setCategory(c.slug)}
                        className={`w-full text-left font-sans text-sm px-3 py-2.5 rounded-sm transition-colors ${
                          activeCategory === c.slug
                            ? "bg-matte-900 text-cream-100"
                            : "text-matte-600 hover:bg-cream-200"
                        }`}
                      >
                        {c.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bg-walnut-900/10 border border-walnut-200 rounded-sm p-5">
                  <MessageSquare size={18} className="text-walnut-500 mb-3" />
                  <h4 className="font-serif text-base text-matte-900 mb-2">
                    Don't see what you need?
                  </h4>
                  <p className="font-sans text-xs text-matte-500 leading-relaxed mb-3">
                    Most projects start as a sketch, photo, or even a vague
                    description. Tell Ananda what you're after.
                  </p>
                  <Link
                    to="/book"
                    className="inline-flex items-center gap-1 font-sans text-xs text-walnut-600 hover:text-walnut-800 transition-colors underline"
                  >
                    <Calendar size={12} />
                    Book a free consultation
                  </Link>
                </div>
              </div>
            </aside>

            <div className="flex-1">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-cream-200">
                <p className="font-sans text-sm text-matte-500 hidden sm:block">
                  {filtered.length} design{filtered.length === 1 ? "" : "s"}
                </p>
                <div className="flex items-center gap-3 ml-auto">
                  <span className="font-sans text-xs text-matte-400">Sort:</span>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="font-sans text-sm text-matte-700 bg-white border border-cream-300 px-3 py-1.5 outline-none focus:border-walnut-400 rounded-sm"
                    aria-label="Sort designs"
                  >
                    {SORT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {filtered.length === 0 ? (
                <div className="text-center py-20">
                  <p className="font-serif text-2xl text-matte-400 mb-4">No designs found</p>
                  <button
                    onClick={() => setCategory("all")}
                    className="font-sans text-sm text-walnut-500 border border-walnut-300 px-5 py-2.5 hover:bg-walnut-500 hover:text-white transition-colors"
                  >
                    View All Designs
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
                  {filtered.map((p, i) => (
                    <DesignCard
                      key={p.id}
                      design={p}
                      delay={i * 50}
                      eager={i < 3}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
